import { BASE_URL } from '../components/AppConfig';

const getAuthToken = () => {
  const sessionToken = sessionStorage.getItem('sessionToken');
  return sessionToken ? `Bearer ${sessionToken}` : '';
};

const authFetch = async (path, options = {}) => {
  const token = getAuthToken();
  if (!token) {
    return { statusCode: '50000', message: 'Not authenticated' };
  }

  try {
    const res = await fetch(`${BASE_URL}${path}`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
        ...(options.headers || {}),
      },
      ...options,
    });
    return res.json().catch(() => ({}));
  } catch {
    return { statusCode: '50000', message: 'Request failed' };
  }
};

export const isMindCricketApiSuccess = (res) =>
  String(res?.statusCode || '') === '10000' || res?.statusCode == null;

export const getMindCricketApiPayload = (res) => res?.data ?? null;

const buildQuery = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    qs.set(key, String(value));
  });
  const str = qs.toString();
  return str ? `?${str}` : '';
};

// ── Mind Cricket analytics ──────────────────────────────────────────────────

/** GET /admin/arcade/mindgame/summary */
export const getMindgameSummary = ({ from, to } = {}) =>
  authFetch(`/admin/arcade/mindgame/summary${buildQuery({ from, to })}`, { method: 'GET' });

/** GET /admin/arcade/mindgame/matches */
export const listMindgameMatches = ({
  scope,
  mode,
  status,
  user_id,
  guest_hash,
  from,
  to,
  limit = 50,
  offset = 0,
} = {}) =>
  authFetch(
    `/admin/arcade/mindgame/matches${buildQuery({
      scope,
      mode,
      status,
      user_id,
      guest_hash,
      from,
      to,
      limit,
      offset,
    })}`,
    { method: 'GET' }
  );

/** GET /admin/arcade/mindgame/matches/:matchId */
export const getMindgameMatch = (matchId) =>
  authFetch(`/admin/arcade/mindgame/matches/${matchId}`, { method: 'GET' });

/** GET /admin/arcade/mindgame/users */
export const listMindgameUsers = ({
  q,
  scope,
  from,
  to,
  limit = 50,
  offset = 0,
} = {}) =>
  authFetch(
    `/admin/arcade/mindgame/users${buildQuery({ q, scope, from, to, limit, offset })}`,
    { method: 'GET' }
  );

// ── Super Over League analytics ─────────────────────────────────────────────

/** GET /admin/arcade/super-over-league/summary */
export const getLeagueSummary = ({ from, to } = {}) =>
  authFetch(`/admin/arcade/super-over-league/summary${buildQuery({ from, to })}`, {
    method: 'GET',
  });

/** GET /admin/arcade/super-over-league/profiles */
export const listLeagueProfiles = ({
  level,
  user_id,
  guest_hash,
  min_trophies,
  from,
  to,
  limit = 50,
  offset = 0,
} = {}) =>
  authFetch(
    `/admin/arcade/super-over-league/profiles${buildQuery({
      level,
      user_id,
      guest_hash,
      min_trophies,
      from,
      to,
      limit,
      offset,
    })}`,
    { method: 'GET' }
  );

/** GET /admin/arcade/super-over-league/profiles/:id */
export const getLeagueProfile = (id) =>
  authFetch(`/admin/arcade/super-over-league/profiles/${id}`, { method: 'GET' });

/** GET /admin/arcade/super-over-league/cycles */
export const listLeagueCycles = ({
  status,
  level,
  outcome,
  profile_id,
  from,
  to,
  limit = 50,
  offset = 0,
} = {}) =>
  authFetch(
    `/admin/arcade/super-over-league/cycles${buildQuery({
      status,
      level,
      outcome,
      profile_id,
      from,
      to,
      limit,
      offset,
    })}`,
    { method: 'GET' }
  );

/** GET /admin/arcade/super-over-league/cycles/:id */
export const getLeagueCycle = (id) =>
  authFetch(`/admin/arcade/super-over-league/cycles/${id}`, { method: 'GET' });

/** GET /admin/arcade/super-over-league/matches */
export const listLeagueMatches = ({
  cycle_id,
  won,
  level,
  from,
  to,
  limit = 50,
  offset = 0,
} = {}) =>
  authFetch(
    `/admin/arcade/super-over-league/matches${buildQuery({
      cycle_id,
      won,
      level,
      from,
      to,
      limit,
      offset,
    })}`,
    { method: 'GET' }
  );

/** GET /admin/arcade/super-over-league/events */
export const listLeagueEvents = ({
  event_type,
  user_id,
  from,
  to,
  limit = 50,
  offset = 0,
} = {}) =>
  authFetch(
    `/admin/arcade/super-over-league/events${buildQuery({
      event_type,
      user_id,
      from,
      to,
      limit,
      offset,
    })}`,
    { method: 'GET' }
  );

// ── League config (Controls) ────────────────────────────────────────────────

/** GET /admin/arcade/super-over-league/config */
export const getLeagueConfig = () =>
  authFetch('/admin/arcade/super-over-league/config', { method: 'GET' });

/** PATCH /admin/arcade/super-over-league/config/economy */
export const patchLeagueEconomy = (body) =>
  authFetch('/admin/arcade/super-over-league/config/economy', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

/** PUT /admin/arcade/super-over-league/config/levels */
export const putLeagueLevels = (levels) =>
  authFetch('/admin/arcade/super-over-league/config/levels', {
    method: 'PUT',
    body: JSON.stringify({ levels }),
  });

/** PATCH /admin/arcade/super-over-league/config/levels/:level */
export const patchLeagueLevel = (level, body) =>
  authFetch(`/admin/arcade/super-over-league/config/levels/${level}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
